/**
 * marco-form.ts — Conversión entre el estado del editor de marcos (todo texto) y el payload del API.
 * Puro: el editor valida con esto y muestra el mensaje según el código de error.
 */
import type { MarcoFertilizacion, ProtocolApplication } from '../../api/protocol.api';

export type MarcoFormError = 'columns' | 'rows' | 'dias' | 'cantidad';
export type MarcoFormResult<T> = { value: T; error?: undefined } | { value?: undefined; error: MarcoFormError };

export interface FertFormColumn { key: string; nombre: string; unidad: string }
export interface FertFormRow { dias: string; tipo: string; cantidades: Record<string, string>; observacion: string }
export interface FertForm { columnas: FertFormColumn[]; filas: FertFormRow[] }

export interface PulvFormApp {
  dias: string; etapa: string; observacion: string; productos: { name: string; dose: string }[];
}

export const DEFAULT_UNIT = 'kg/ha';
export const TIPO_BASE = 'fertilizante base';
export const TIPO_GOTEO = 'fertilizante que gotea';

let seq = 0;
/** Clave estable de columna; solo necesita ser única dentro del marco. */
export const newColumnKey = (): string => `c${Date.now().toString(36)}${(seq++).toString(36)}`;

export const emptyFertRow = (): FertFormRow => ({ dias: '', tipo: TIPO_GOTEO, cantidades: {}, observacion: '' });
export const emptyFertForm = (): FertForm => ({
  columnas: [{ key: newColumnKey(), nombre: '', unidad: DEFAULT_UNIT }],
  filas: [{ ...emptyFertRow(), dias: '-1', tipo: TIPO_BASE }],
});

export function fertToForm(m?: MarcoFertilizacion): FertForm {
  if (!m?.columnas?.length) return emptyFertForm();
  return {
    columnas: m.columnas.map(c => ({ ...c })),
    filas: m.filas.map(f => ({
      dias: String(f.dias),
      tipo: f.tipo,
      cantidades: Object.fromEntries(Object.entries(f.cantidades).map(([k, v]) => [k, String(v)])),
      observacion: f.observacion ?? '',
    })),
  };
}

/** Acepta coma decimal ("12,5"); vacío = sin valor. */
const parseNum = (s: string): number | null | 'invalid' => {
  const t = s.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : 'invalid';
};

export function fertFromForm(f: FertForm): MarcoFormResult<MarcoFertilizacion> {
  const columnas = f.columnas.filter(c => c.nombre.trim()).map(c => ({
    key: c.key, nombre: c.nombre.trim(), unidad: c.unidad.trim() || DEFAULT_UNIT,
  }));
  if (columnas.length === 0) return { error: 'columns' };
  if (f.filas.length === 0) return { error: 'rows' };

  const filas: MarcoFertilizacion['filas'] = [];
  for (const row of f.filas) {
    const dias = parseNum(row.dias);
    if (dias === null || dias === 'invalid') return { error: 'dias' };
    const cantidades: Record<string, number> = {};
    for (const c of columnas) {
      const q = parseNum(row.cantidades[c.key] ?? '');
      if (q === 'invalid' || (q !== null && q < 0)) return { error: 'cantidad' };
      if (q !== null && q > 0) cantidades[c.key] = q;
    }
    const observacion = row.observacion.trim();
    filas.push({
      dias,
      tipo: row.tipo.trim() || TIPO_GOTEO,
      cantidades,
      ...(observacion ? { observacion } : {}),
    });
  }
  return { value: { columnas, filas } };
}

export const emptyPulvApp = (): PulvFormApp => ({ dias: '', etapa: '', observacion: '', productos: [{ name: '', dose: '' }] });

export function pulvToForm(apps?: ProtocolApplication[]): PulvFormApp[] {
  if (!apps?.length) return [emptyPulvApp()];
  return apps.map(a => ({
    dias: a.dias,
    etapa: a.etapa ?? '',
    observacion: a.observacion ?? '',
    productos: a.productos.length ? a.productos.map(p => ({ name: p.name, dose: p.dose })) : [{ name: '', dose: '' }],
  }));
}

export function pulvFromForm(apps: PulvFormApp[]): MarcoFormResult<ProtocolApplication[]> {
  const out: ProtocolApplication[] = [];
  for (const a of apps) {
    const productos = a.productos.map(p => ({ name: p.name.trim(), dose: p.dose.trim() })).filter(p => p.name);
    if (!a.dias.trim() && productos.length === 0) continue; // fila vacía: se ignora
    if (!a.dias.trim()) return { error: 'dias' };
    out.push({
      dias: a.dias.trim(),
      productos,
      costoHa: 0,
      ...(a.etapa.trim() ? { etapa: a.etapa.trim() } : {}),
      ...(a.observacion.trim() ? { observacion: a.observacion.trim() } : {}),
    });
  }
  return out.length ? { value: out } : { error: 'rows' };
}
